
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload } from "lucide-react";
import { ImageUpload } from "@/components/shared/ImageUpload";

export const DocumentUpload = () => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [governmentIdUrl, setGovernmentIdUrl] = useState<string | null>(null);
  const [addressProofUrl, setAddressProofUrl] = useState<string | null>(null);
  const [documentNumber, setDocumentNumber] = useState("");

  const handleGovernmentIdUpload = (urls: string[]) => {
    if (urls.length > 0) {
      setGovernmentIdUrl(urls[0]);
    }
  };

  const handleAddressProofUpload = (urls: string[]) => {
    if (urls.length > 0) {
      setAddressProofUrl(urls[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentNumber) {
      toast({
        title: "Error",
        description: "Please enter a document number.",
        variant: "destructive",
      });
      return;
    }
    
    if (!governmentIdUrl && !addressProofUrl) {
      toast({
        title: "Error",
        description: "Please upload at least one document.",
        variant: "destructive",
      });
      return;
    }
    
    setIsUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: verificationError } = await supabase
        .from('verification_documents')
        .insert({
          user_id: user.id,
          document_type: 'host_verification',
          document_number: documentNumber,
          front_image: governmentIdUrl,
          back_image: addressProofUrl,
          status: 'pending'
        });

      if (verificationError) throw verificationError;

      toast({
        title: "Documents uploaded",
        description: "Your verification documents have been submitted for review.",
      });

      // Update host verification requirements
      await supabase
        .from('host_verification_requirements')
        .upsert({
          host_id: user.id,
          government_id_submitted: !!governmentIdUrl,
          address_proof_submitted: !!addressProofUrl,
        });

    } catch (error) {
      console.error('Error uploading documents:', error);
      toast({
        title: "Error",
        description: "Failed to upload verification documents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verification Documents</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="documentNumber">Document Number</Label>
              <Input
                id="documentNumber"
                type="text"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder="Enter your ID number"
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                Enter the number shown on your government ID
              </p>
            </div>

            <div>
              <Label htmlFor="governmentId">Government ID</Label>
              <div className="mt-1">
                <ImageUpload
                  bucketName="verification-documents"
                  onUploadComplete={handleGovernmentIdUpload}
                  maxFileSize={5}
                  allowMultiple={false}
                  buttonText="Upload Government ID"
                  filePrefix="gov-id-"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Upload a clear photo or scan of your government-issued ID
              </p>
              {governmentIdUrl && (
                <div className="mt-2 border rounded-md p-2">
                  <p className="text-sm text-green-600">Document uploaded successfully</p>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="addressProof">Proof of Address</Label>
              <div className="mt-1">
                <ImageUpload
                  bucketName="verification-documents"
                  onUploadComplete={handleAddressProofUpload}
                  maxFileSize={5}
                  allowMultiple={false}
                  buttonText="Upload Proof of Address"
                  filePrefix="address-"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Upload a recent utility bill or bank statement showing your address
              </p>
              {addressProofUrl && (
                <div className="mt-2 border rounded-md p-2">
                  <p className="text-sm text-green-600">Document uploaded successfully</p>
                </div>
              )}
            </div>
          </div>

          <Button type="submit" disabled={isUploading} className="w-full">
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Submit Documents
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
